import { Bubble } from './bubble';
import { Configurations } from './types';

/**
 * Toast shows a speech bubble.
 */
class Toast {
  element: HTMLElement;
  private timeoutID: number;
  private placeholderTimeoutValue: number;

  constructor(config: Configurations) {
    this.element = Template.defaultToastTemplate(
      config.placeholder.avatar.toast.message,
    );
    this.placeholderTimeoutValue =
      config.placeholder.avatar.toast.placeholderTimeout;
    this.init();

    window.addEventListener('kauz.chat.close', (_event: CustomEvent) => {
      this.element.innerHTML = _event.detail.avatarMessage;
    });
  }

  init(): void {
    this.element.classList.remove('invisible');

    this.timeoutID = window.setTimeout(() => {
      this.element.classList.add('invisible');
    }, this.placeholderTimeoutValue);

    window.addEventListener('kauz.chat.open', (_event: CustomEvent) => {
      this.element.classList.add('invisible');
    });
    window.addEventListener(
      'kauz.avatar.enter',
      (_event: CustomEvent<Bubble>) => {
        window.clearTimeout(this.timeoutID);
        this.element.classList.remove('invisible');
      },
    );
    window.addEventListener('kauz.avatar.leave', (_event: CustomEvent) => {
      this.element.classList.add('invisible');
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }
}

/**
 * Avatar comprises a chatbot placeholder avatar image and a speech bubble.
 */
export class Avatar {
  public element: HTMLElement;
  private image: HTMLElement;
  private config: Configurations;
  private toast: Toast;

  constructor(config: Configurations) {
    this.element = Template.defaultAvatarTemplate();
    this.config = this.applyDefaultOptions(config);

    this.init(config.placeholder.avatar.toast.message);
  }

  private applyDefaultOptions(config: Configurations): Configurations {
    return Object.assign({}, {}, config);
  }

  private init(message: string): void {
    if (this.config.placeholder.avatar.toast) {
      this.toast = new Toast(this.config);
      this.element.appendChild(this.toast.element);
    }

    this.image = document.createElement('div');
    this.image.classList.add('bot', 'avatar');
    this.image.setAttribute('role', 'button');
    this.image.setAttribute('aria-label', message);
    this.image.setAttribute('tabindex', '0');

    this.element.appendChild(this.image);

    window.addEventListener('kauz.chat.open', (_: CustomEvent) => {
      this.element.classList.add('invisible');
    });
    window.addEventListener('kauz.chat.close', (_: CustomEvent) => {
      this.element.classList.remove('invisible');
    });

    this.registerHandlers();
  }

  private registerHandlers(): void {
    this.image.addEventListener('mouseenter', (_: MouseEvent) => {
      window.dispatchEvent(
        new CustomEvent('kauz.avatar.enter', { detail: this }),
      );
    });
    this.image.addEventListener('mouseleave', (_: MouseEvent) => {
      window.dispatchEvent(
        new CustomEvent('kauz.avatar.leave', { detail: this }),
      );
    });
    this.image.addEventListener('click', (_: MouseEvent) => {
      window.dispatchEvent(new CustomEvent('kauz.chat.open', { detail: this }));
    });

    this.image.addEventListener('keydown', (event: KeyboardEvent) => {
      const { key } = event;

      if (key === 'Enter' || key === ' ') {
        event.preventDefault();

        window.dispatchEvent(
          new CustomEvent('kauz.chat.open', { detail: this }),
        );
      }
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }
}

/**
 * Template holds HTML elements used by chatbot placeholder UI.
 */
class Template {
  static defaultAvatarTemplate(): HTMLElement {
    const element = document.createElement('div');
    element.id = 'placeholder';

    return element;
  }

  static defaultToastTemplate(message: string): HTMLElement {
    const element = document.createElement('div');
    element.id = 'greeting';

    element.innerHTML = message;

    return element;
  }
}
